/**
 * Image store — IndexedDB local cache + Firebase Storage for cross-device sync.
 *
 * Storage path: users/{uid}/images/{docId}/{encodedFilename}
 * Filenames are percent-encoded so '/' in paths doesn't create fake subdirectories.
 *
 * Manifests (lists of filenames per docId) are kept in:
 *   - localStorage: fast local access after first import
 *   - Firestore (via StoredDocument.imageFiles): enables new-device discovery
 */

import { getStorage, ref, uploadString, getBlob, deleteObject } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { getFirebaseApp, isDemoMode } from "../config/firebase";

// ─── IndexedDB local cache ────────────────────────────────────────────────────

const DB_NAME = "seetex-images";
const STORE_NAME = "images";
const DB_VERSION = 1;

let _db: IDBDatabase | null = null;

function openDB(): Promise<IDBDatabase> {
    if (_db) return Promise.resolve(_db);
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => { req.result.createObjectStore(STORE_NAME); };
        req.onsuccess = () => { _db = req.result; resolve(req.result); };
        req.onerror = () => reject(req.error);
    });
}

function idbKey(docId: string, filename: string): string {
    return `${docId}\0${filename}`;
}

async function idbPut(docId: string, filename: string, dataUrl: string): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).put(dataUrl, idbKey(docId, filename));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

async function idbGetAll(docId: string): Promise<Record<string, string>> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const prefix = `${docId}\0`;
        const range = IDBKeyRange.bound(prefix, prefix + "\uffff");
        const req = tx.objectStore(STORE_NAME).openCursor(range);
        const result: Record<string, string> = {};
        req.onsuccess = () => {
            const cursor = req.result;
            if (cursor) {
                result[(cursor.key as string).slice(prefix.length)] = cursor.value as string;
                cursor.continue();
            } else {
                resolve(result);
            }
        };
        req.onerror = () => reject(req.error);
    });
}

async function idbDeleteAll(docId: string): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const prefix = `${docId}\0`;
        const range = IDBKeyRange.bound(prefix, prefix + "\uffff");
        tx.objectStore(STORE_NAME).delete(range);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

// ─── Manifest (filename list per doc) ────────────────────────────────────────
// Stored in localStorage so we know which filenames to fetch from Firebase Storage
// on a device that already has the manifest (e.g. the device that did the import).
// On a brand-new device, the manifest comes from StoredDocument.imageFiles (Firestore).

const MANIFEST_PREFIX = "seetex-imglist-";

/** Populate the local manifest from Firestore data (called by App.tsx on doc select). */
export function setManifest(docId: string, filenames: string[]): void {
    try { localStorage.setItem(MANIFEST_PREFIX + docId, JSON.stringify(filenames)); } catch { }
}

function getManifest(docId: string): string[] {
    try {
        const raw = localStorage.getItem(MANIFEST_PREFIX + docId);
        return raw ? (JSON.parse(raw) as string[]) : [];
    } catch { return []; }
}

function clearManifest(docId: string): void {
    localStorage.removeItem(MANIFEST_PREFIX + docId);
}

// ─── Firebase Storage helpers ─────────────────────────────────────────────────

function storageRef(uid: string, docId: string, filename: string) {
    return ref(getStorage(getFirebaseApp()), `users/${uid}/images/${docId}/${encodeURIComponent(filename)}`);
}

async function uploadToStorage(uid: string, docId: string, filename: string, dataUrl: string): Promise<void> {
    await uploadString(storageRef(uid, docId, filename), dataUrl, "data_url");
}

async function downloadFromStorage(uid: string, docId: string, filename: string): Promise<string> {
    const blob = await getBlob(storageRef(uid, docId, filename));
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

async function deleteFromStorage(uid: string, docId: string, filename: string): Promise<void> {
    await deleteObject(storageRef(uid, docId, filename)).catch(() => {/* ignore not-found */ });
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Save images for a document.
 * - Always caches in IndexedDB (instant local access).
 * - If `isPro` is true and user is authenticated, also uploads to Firebase Storage (cross-device sync).
 * - Free users get local-only storage.
 */
export async function saveDocImages(docId: string, images: Record<string, string>, isPro = false): Promise<void> {
    const filenames = Object.keys(images);

    // 1. Always save to IndexedDB (fast local access for everyone)
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        for (const [filename, dataUrl] of Object.entries(images)) {
            store.put(dataUrl, idbKey(docId, filename));
        }
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });

    // 2. Firebase Storage upload — Pro users only
    if (!isPro || isDemoMode()) return;

    const uid = getAuth(getFirebaseApp()).currentUser?.uid;
    if (!uid) return;

    // 3. Update local manifest
    setManifest(docId, filenames);

    // 4. Upload to Firebase Storage in parallel
    await Promise.all(
        filenames.map((f) => uploadToStorage(uid, docId, f, images[f]).catch(() => {}))
    );
}

/**
 * Load images for a document.
 * Returns cached data URLs from IndexedDB if available; otherwise downloads
 * from Firebase Storage (using the manifest to discover filenames) and caches locally.
 */
export async function loadDocImages(docId: string): Promise<Record<string, string>> {
    // Fast path: return from IndexedDB cache
    const cached = await idbGetAll(docId);
    if (Object.keys(cached).length > 0) return cached;

    if (isDemoMode()) return {};

    const uid = getAuth(getFirebaseApp()).currentUser?.uid;
    if (!uid) return {};

    const filenames = getManifest(docId);
    if (filenames.length === 0) return {};

    // Download from Firebase Storage, populate IndexedDB, return results
    const result: Record<string, string> = {};
    await Promise.all(
        filenames.map(async (filename) => {
            try {
                const dataUrl = await downloadFromStorage(uid, docId, filename);
                await idbPut(docId, filename, dataUrl);
                result[filename] = dataUrl;
            } catch (e) {
                if (import.meta.env.DEV) console.warn(`[SeeTex] image download failed "${filename}":`, e);
            }
        })
    );
    return result;
}

/**
 * Delete all images for a document from IndexedDB and Firebase Storage.
 */
export async function deleteDocImages(docId: string): Promise<void> {
    await idbDeleteAll(docId);

    if (isDemoMode()) { clearManifest(docId); return; }

    const uid = getAuth(getFirebaseApp()).currentUser?.uid;
    const filenames = getManifest(docId);
    if (uid && filenames.length > 0) {
        await Promise.all(filenames.map((f) => deleteFromStorage(uid, docId, f)));
    }
    clearManifest(docId);
}
